import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Droplets, Info, Thermometer, Shield, Zap, Maximize } from 'lucide-react';
export function MaterialsPage() {
  const [activeTab, setActiveTab] = useState<'fdm' | 'resin'>('fdm');
  const fdmMaterials = [{
    name: 'PLA (Polylactic Acid)',
    tag: 'Standard',
    description: 'The most common 3D printing material. Easy to print, biodegradable, and offers great detail.',
    properties: {
      strength: 'Medium',
      flexibility: 'Low',
      heatResist: 'Low (60°C)',
      difficulty: 'Easy'
    },
    bestFor: ['Visual prototypes', 'Concept models', 'Non-functional parts', 'Large prints'],
    pros: ['Excellent surface finish', 'Dimensionally accurate', 'Cost-effective'],
    cons: ['Brittle', 'Low heat resistance', 'Not UV stable']
  }, {
    name: 'ABS (Acrylonitrile Butadiene Styrene)',
    tag: 'Engineering',
    description: 'A tough, impact-resistant plastic commonly used in injection molding (like LEGO bricks).',
    properties: {
      strength: 'High',
      flexibility: 'Medium',
      heatResist: 'High (100°C)',
      difficulty: 'Medium'
    },
    bestFor: ['Functional prototypes', 'End-use parts', 'Enclosures', 'Interlocking parts'],
    pros: ['High impact resistance', 'Good heat resistance', 'Can be vapor smoothed'],
    cons: ['Prone to warping', 'Produces fumes during printing']
  }, {
    name: 'PETG (Polyethylene Terephthalate Glycol)',
    tag: 'Standard',
    description: 'Combines the ease of PLA with the strength and temperature resistance of ABS.',
    properties: {
      strength: 'High',
      flexibility: 'Medium',
      heatResist: 'Medium (80°C)',
      difficulty: 'Easy'
    },
    bestFor: ['Mechanical parts', 'Snap-fit components', 'Water-resistant applications'],
    pros: ['Excellent layer adhesion', 'Chemical resistant', 'Durable and slightly flexible'],
    cons: ['Prone to stringing', 'Poor bridging capabilities']
  }, {
    name: 'TPU (Thermoplastic Polyurethane)',
    tag: 'Flexible',
    description: 'A highly flexible, rubber-like material that can withstand significant stretching and compression.',
    properties: {
      strength: 'Medium',
      flexibility: 'Very High',
      heatResist: 'Medium (80°C)',
      difficulty: 'Hard'
    },
    bestFor: ['Gaskets', 'Phone cases', 'Tires/Treads', 'Vibration dampeners'],
    pros: ['Extreme flexibility', 'High impact resistance', 'Abrasion resistant'],
    cons: ['Difficult to print', 'Slow print speeds required', 'Poor bridging']
  }, {
    name: 'Nylon (Polyamide)',
    tag: 'Engineering',
    description: 'An incredibly strong, durable, and versatile material with low friction properties.',
    properties: {
      strength: 'Very High',
      flexibility: 'High',
      heatResist: 'High (120°C)',
      difficulty: 'Hard'
    },
    bestFor: ['Gears', 'Bearings', 'Living hinges', 'High-stress components'],
    pros: ['Exceptional strength', 'High wear resistance', 'Chemical resistant'],
    cons: ['Highly hygroscopic (absorbs moisture)', 'Prone to warping']
  }, {
    name: 'ASA (Acrylic Styrene Acrylonitrile)',
    tag: 'Engineering',
    description: 'Similar to ABS but with exceptional UV resistance, making it perfect for outdoor applications.',
    properties: {
      strength: 'High',
      flexibility: 'Medium',
      heatResist: 'High (100°C)',
      difficulty: 'Medium'
    },
    bestFor: ['Outdoor enclosures', 'Automotive parts', 'Sporting goods'],
    pros: ['UV resistant', 'High impact strength', 'Good heat resistance'],
    cons: ['Requires enclosure to print', 'Produces fumes']
  }, {
    name: 'Carbon Fiber Composites',
    tag: 'Specialty',
    description: 'Base materials (like PLA, PETG, or Nylon) infused with chopped carbon fibers for extreme rigidity.',
    properties: {
      strength: 'Extreme',
      flexibility: 'Very Low',
      heatResist: 'Varies by base',
      difficulty: 'Medium'
    },
    bestFor: ['Drone frames', 'RC parts', 'Structural components', 'Jigs and fixtures'],
    pros: ['Extremely rigid', 'Lightweight', 'Excellent dimensional stability'],
    cons: ['Abrasive to nozzles', 'Brittle compared to pure base material']
  }];
  const resinMaterials = [{
    name: 'Standard Resin',
    tag: 'Standard',
    description: 'Provides exceptional detail and a smooth surface finish. Ideal for visual models.',
    properties: {
      detail: 'Extreme',
      toughness: 'Low',
      flexibility: 'Low',
      heatResist: 'Low'
    },
    bestFor: ['Miniatures', 'Visual prototypes', 'Concept models', 'Art pieces'],
    pros: ['Incredible detail resolution', 'Smooth surface finish', 'Fast printing'],
    cons: ['Brittle', 'Low impact resistance', 'Can degrade in sunlight over time']
  }, {
    name: 'Tough / ABS-Like Resin',
    tag: 'Engineering',
    description: 'Formulated to mimic the mechanical properties of ABS plastic, offering better impact resistance.',
    properties: {
      detail: 'High',
      toughness: 'High',
      flexibility: 'Medium',
      heatResist: 'Medium'
    },
    bestFor: ['Functional prototypes', 'Snap-fit joints', 'Enclosures', 'Mechanical parts'],
    pros: ['Good impact resistance', 'Less brittle than standard', 'Machinable'],
    cons: ['More expensive', 'Requires precise post-curing']
  }, {
    name: 'Flexible / Rubber-Like Resin',
    tag: 'Specialty',
    description: 'Simulates rubber or silicone, allowing parts to bend and compress without breaking.',
    properties: {
      detail: 'Medium',
      toughness: 'Medium',
      flexibility: 'High',
      heatResist: 'Low'
    },
    bestFor: ['Gaskets', 'Wearables', 'Stamps', 'Overmolding prototypes'],
    pros: ['High elasticity', 'Tear resistant', 'Can be mixed with standard resin'],
    cons: ['Difficult to support', 'Requires specialized cleaning']
  }, {
    name: 'High-Temp Resin',
    tag: 'Engineering',
    description: 'Designed to withstand high temperatures (often up to 230°C+) without deflecting under load.',
    properties: {
      detail: 'High',
      toughness: 'Medium',
      flexibility: 'Low',
      heatResist: 'Extreme'
    },
    bestFor: ['Mold masters', 'Hot air/fluid routing', 'Thermoforming tools'],
    pros: ['Extreme heat resistance', 'High stiffness', 'Smooth finish'],
    cons: ['Very brittle', 'Requires thermal post-curing']
  }, {
    name: 'Castable Resin',
    tag: 'Specialty',
    description: 'Burns out cleanly without ash residue, designed specifically for investment casting.',
    properties: {
      detail: 'Extreme',
      toughness: 'Low',
      flexibility: 'Low',
      heatResist: 'N/A'
    },
    bestFor: ['Jewelry making', 'Dental casting', 'Metal part prototyping'],
    pros: ['Zero ash burnout', 'Captures micro-details', 'Smooth surfaces'],
    cons: ['Fragile before casting', 'Requires specific burnout schedules']
  }];
  return <div className="min-h-screen bg-sandev-bg" data-id="element-389">
      {/* Header */}
      <section className="pt-24 pb-16 bg-sandev-card border-b border-sandev-border" data-id="element-390">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-391">
          <div className="max-w-3xl" data-id="element-392">
            <h1 className="text-4xl md:text-5xl font-bold text-sandev-text mb-6" data-id="element-393">
              Materials Guide
            </h1>
            <p className="text-xl text-sandev-muted leading-relaxed" data-id="element-394">
              Choosing the right material is critical for your prototype's
              success. Explore our comprehensive range of FDM filaments and
              photopolymer resins to find the perfect match for your
              application.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="sticky top-20 z-40 bg-sandev-bg/95 backdrop-blur-md border-b border-sandev-border py-4" data-id="element-395">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-396">
          <div className="flex space-x-4" data-id="element-397">
            <button onClick={() => setActiveTab('fdm')} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === 'fdm' ? 'bg-sandev-accent text-white shadow-[0_0_15px_rgba(0,180,216,0.3)]' : 'bg-sandev-card text-sandev-muted hover:text-sandev-text hover:bg-sandev-cardHover border border-sandev-border'}`} data-id="element-398">
              <Layers size={20} data-id="element-399" />
              FDM Filaments
            </button>
            <button onClick={() => setActiveTab('resin')} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === 'resin' ? 'bg-sandev-accent text-white shadow-[0_0_15px_rgba(0,180,216,0.3)]' : 'bg-sandev-card text-sandev-muted hover:text-sandev-text hover:bg-sandev-cardHover border border-sandev-border'}`} data-id="element-400">
              <Droplets size={20} data-id="element-401" />
              Resin Types
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-id="element-402">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-403">
          {/* FDM Section */}
          {activeTab === 'fdm' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="space-y-8" data-id="element-404">
              <div className="mb-10" data-id="element-405">
                <h2 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-406">
                  Fused Deposition Modeling (FDM)
                </h2>
                <p className="text-sandev-muted max-w-3xl" data-id="element-407">
                  FDM works by extruding molten thermoplastic layer by layer. It
                  is the most cost-effective method for producing durable,
                  functional parts and offers the widest variety of
                  engineering-grade materials.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-408">
                {fdmMaterials.map((mat, idx) => <div key={idx} className="bg-sandev-card border border-sandev-border rounded-xl p-6 hover:border-sandev-accent/50 transition-colors" data-id="element-409">
                    <div className="flex justify-between items-start mb-4" data-id="element-410">
                      <h3 className="text-xl font-bold text-sandev-text" data-id="element-411">
                        {mat.name}
                      </h3>
                      <span className="px-3 py-1 bg-sandev-bg border border-sandev-border rounded-full text-xs font-medium text-sandev-accent" data-id="element-412">
                        {mat.tag}
                      </span>
                    </div>
                    <p className="text-sandev-muted text-sm mb-6 h-10" data-id="element-413">
                      {mat.description}
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-414">
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-415">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-416">
                          <Shield size={14} data-id="element-417" /> Strength
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-418">
                          {mat.properties.strength}
                        </div>
                      </div>
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-419">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-420">
                          <Maximize size={14} data-id="element-421" /> Flexibility
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-422">
                          {mat.properties.flexibility}
                        </div>
                      </div>
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-423">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-424">
                          <Thermometer size={14} data-id="element-425" /> Heat Resist
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-426">
                          {mat.properties.heatResist}
                        </div>
                      </div>
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-427">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-428">
                          <Zap size={14} data-id="element-429" /> Printability
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-430">
                          {mat.properties.difficulty}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-431">
                      <div data-id="element-432">
                        <h4 className="text-xs font-bold text-sandev-text uppercase tracking-wider mb-2 flex items-center gap-1" data-id="element-433">
                          <Info size={14} className="text-sandev-accent" data-id="element-434" /> Best
                          For
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-435">
                          {mat.bestFor.map((use, i) => <span key={i} className="text-xs px-2 py-1 bg-sandev-bg rounded text-sandev-muted border border-sandev-border" data-id="element-436">
                              {use}
                            </span>)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-sandev-border" data-id="element-437">
                        <div data-id="element-438">
                          <h4 className="text-xs font-bold text-green-400 uppercase tracking-wider mb-2" data-id="element-439">
                            Pros
                          </h4>
                          <ul className="text-xs text-sandev-muted space-y-1" data-id="element-440">
                            {mat.pros.map((pro, i) => <li key={i} data-id="element-441">• {pro}</li>)}
                          </ul>
                        </div>
                        <div data-id="element-442">
                          <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-2" data-id="element-443">
                            Cons
                          </h4>
                          <ul className="text-xs text-sandev-muted space-y-1" data-id="element-444">
                            {mat.cons.map((con, i) => <li key={i} data-id="element-445">• {con}</li>)}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </motion.div>}

          {/* Resin Section */}
          {activeTab === 'resin' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="space-y-8" data-id="element-446">
              <div className="mb-10" data-id="element-447">
                <h2 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-448">
                  SLA / MSLA Resin Printing
                </h2>
                <p className="text-sandev-muted max-w-3xl" data-id="element-449">
                  Resin printing uses UV light to cure liquid photopolymer resin
                  layer by layer. It produces parts with incredible resolution,
                  microscopic details, and injection-mold-like surface finishes.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-450">
                {resinMaterials.map((mat, idx) => <div key={idx} className="bg-sandev-card border border-sandev-border rounded-xl p-6 hover:border-sandev-accent/50 transition-colors" data-id="element-451">
                    <div className="flex justify-between items-start mb-4" data-id="element-452">
                      <h3 className="text-xl font-bold text-sandev-text" data-id="element-453">
                        {mat.name}
                      </h3>
                      <span className="px-3 py-1 bg-sandev-bg border border-sandev-border rounded-full text-xs font-medium text-sandev-accent" data-id="element-454">
                        {mat.tag}
                      </span>
                    </div>
                    <p className="text-sandev-muted text-sm mb-6 h-10" data-id="element-455">
                      {mat.description}
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-456">
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-457">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-458">
                          <Zap size={14} data-id="element-459" /> Detail Level
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-460">
                          {mat.properties.detail}
                        </div>
                      </div>
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-461">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-462">
                          <Shield size={14} data-id="element-463" /> Toughness
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-464">
                          {mat.properties.toughness}
                        </div>
                      </div>
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-465">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-466">
                          <Maximize size={14} data-id="element-467" /> Flexibility
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-468">
                          {mat.properties.flexibility}
                        </div>
                      </div>
                      <div className="bg-sandev-bg p-3 rounded-lg border border-sandev-border" data-id="element-469">
                        <div className="flex items-center gap-2 text-xs text-sandev-muted mb-1" data-id="element-470">
                          <Thermometer size={14} data-id="element-471" /> Heat Resist
                        </div>
                        <div className="font-semibold text-sandev-text text-sm" data-id="element-472">
                          {mat.properties.heatResist}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-473">
                      <div data-id="element-474">
                        <h4 className="text-xs font-bold text-sandev-text uppercase tracking-wider mb-2 flex items-center gap-1" data-id="element-475">
                          <Info size={14} className="text-sandev-accent" data-id="element-476" /> Best
                          For
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-477">
                          {mat.bestFor.map((use, i) => <span key={i} className="text-xs px-2 py-1 bg-sandev-bg rounded text-sandev-muted border border-sandev-border" data-id="element-478">
                              {use}
                            </span>)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-sandev-border" data-id="element-479">
                        <div data-id="element-480">
                          <h4 className="text-xs font-bold text-green-400 uppercase tracking-wider mb-2" data-id="element-481">
                            Pros
                          </h4>
                          <ul className="text-xs text-sandev-muted space-y-1" data-id="element-482">
                            {mat.pros.map((pro, i) => <li key={i} data-id="element-483">• {pro}</li>)}
                          </ul>
                        </div>
                        <div data-id="element-484">
                          <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-2" data-id="element-485">
                            Cons
                          </h4>
                          <ul className="text-xs text-sandev-muted space-y-1" data-id="element-486">
                            {mat.cons.map((con, i) => <li key={i} data-id="element-487">• {con}</li>)}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </motion.div>}
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-sandev-card border-t border-sandev-border" data-id="element-488">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-489">
          <h3 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-490">
            Not sure which material to choose?
          </h3>
          <p className="text-sandev-muted mb-8" data-id="element-491">
            Our engineering team can help analyze your part requirements and
            recommend the optimal material and printing technology for your
            specific use case.
          </p>
          <button className="px-8 py-3 bg-sandev-bg border border-sandev-accent text-sandev-accent hover:bg-sandev-accent hover:text-white rounded-md font-semibold transition-all" data-id="element-492">
            Contact an Engineer
          </button>
        </div>
      </section>
    </div>;
}